// @givmo/board — the officer's reads of a board's compensation decisions (CBG-YR4-PAY, -PAY-CONSENT),
// each dark until the backend serves it (the self-assessment's shape, `evaluations/assessment-queries.ts`).
//
// ══ A ROUTE MISS IS "NOT SERVED", NEVER AN ERROR ════════════════════════════════════════════════
//
// The compensation routes ride the Board module's own flag; a backend older than be#3118 has none of
// them. Either way each read here RESOLVES the absent-route 404 to `null`, a settled answer meaning
// "not served", so the motion card and the consent page draw nothing of it and the list says one
// quiet line, with nothing retrying. Any other failure stays an error.
//
// One key family, `boardCompensationKeys`, so one act refreshes every read of its organization's
// decisions: a motion's details reach the list, and the list's labels reach the motion card.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BoardCompensationPersonOut } from '@givmo/api-client';
import { useBoardOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import {
  decodeConsentCompensation,
  decodeMeetingCompensation,
  extractPeople,
  type ConsentCompensation,
  type MeetingCompensation,
} from './compensation-decode';

/** The compensation decisions' query keys, rooted per organization so one act can refresh them all. */
export function boardCompensationKeys(orgRef: string | null) {
  const root = ['board-compensation', orgRef ?? ''] as const;
  return {
    root,
    list: [...root, 'list'] as const,
    meeting: (meetingId: string) => [...root, 'meeting', meetingId] as const,
    consent: (consentId: string) => [...root, 'consent', consentId] as const,
  };
}

/** Never retry the route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failureCount: number, error: unknown): boolean {
  return !isNotServed(error) && failureCount < 1;
}

async function servedOrNull<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (error) {
    if (isNotServed(error)) return null;
    throw error;
  }
}

/** Every decision this reader may read, grouped by person (frame c2): `null` while not served. */
export function useCompensationList(orgRef: string | null, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCompensationKeys(orgRef).list,
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: (): Promise<BoardCompensationPersonOut[] | null> =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardCompensationDecisions({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return extractPeople(data);
      }),
  });
}

/** A meeting's decisions and the papers they may name (frame c1): `null` while not served. */
export function useMeetingCompensation(orgRef: string | null, meetingId: string, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCompensationKeys(orgRef).meeting(meetingId),
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: (): Promise<MeetingCompensation | null> =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardMeetingCompensationDecisions({
          path: { org_ref: orgRef ?? '', meeting_id: meetingId },
          throwOnError: true,
        });
        return decodeMeetingCompensation(data);
      }),
  });
}

/** A consent's decisions, their documents and the one block its text must carry: `null` while not
 *  served. */
export function useConsentCompensation(orgRef: string | null, consentId: string, enabled = true) {
  const operations = useBoardOperations();
  return useQuery({
    queryKey: boardCompensationKeys(orgRef).consent(consentId),
    enabled: enabled && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: (): Promise<ConsentCompensation | null> =>
      servedOrNull(async () => {
        const { data } = await operations.officerListBoardConsentCompensationDecisions({
          path: { org_ref: orgRef ?? '', consent_id: consentId },
          throwOnError: true,
        });
        return decodeConsentCompensation(data);
      }),
  });
}

/** Refresh every read of the organization's compensation decisions after an act. */
export function useRefreshCompensation(orgRef: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: boardCompensationKeys(orgRef).root });
}
